import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });

  const formActionUrl = 'https://readdy.ai/api/form/d9pbqbgsenbniolessfg';

  const handleContactSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const honeypotEl = formEl.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      return;
    }
    const formData = new FormData(formEl);
    fetch(formActionUrl, {
      method: 'POST',
      body: new URLSearchParams(formData as unknown as Record<string, string>),
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })
      .then((res) => res.text())
      .then((responseText) => {
        let parsed: unknown;
        try { parsed = JSON.parse(responseText); } catch { parsed = {}; }
        const code = (parsed as Record<string, unknown>)?.code as string | undefined;
        if (res.ok && code === 'OK') {
          setFormStatus('success');
          setContactForm({ name: '', email: '', message: '' });
        } else {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        }
      })
      .catch(() => {
        setFormStatus('error');
        setFormError('网络错误，请稍后重试');
      });
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 韩漫大全',
    description: '韩漫大全品牌介绍、用户口碑与联系方式，提供韩漫和日漫免费在线阅读服务',
    url: window.location.href,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫大全',
      url: window.location.origin,
    },
    about: {
      '@type': 'Organization',
      name: '韩漫大全',
      description: '提供韩漫和日漫在线免费阅读的综合性漫画平台',
      url: window.location.origin,
    },
    inLanguage: 'zh-CN',
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫大全是一个什么样的平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫大全是一个专注于韩漫和日漫在线免费阅读的中文漫画平台，拥有丰富的韩漫大全和好看的韩国漫画资源，所有内容均为无删减版本，更新迅速。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫大全的漫画更新速度快吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫大全与韩国日本同步更新，每日都有新内容上架，确保读者能够第一时间获取最新的韩漫和日漫章节。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
        </div>

        {/* Hero */}
        <section className="relative py-16 md:py-24 px-4 md:px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20artistic%20library%20setting%20with%20comic%20books%20and%20manga%20on%20wooden%20shelves%2C%20soft%20natural%20lighting%2C%20cozy%20reading%20atmosphere%2C%20warm%20amber%20and%20cream%20tones%2C%20clean%20minimalist%20composition%2C%20editorial%20photography%20style&width=1600&height=600&seq=about-hero-bg&orientation=landscape"
              alt="韩漫大全品牌背景"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          </div>
          <div className="relative z-10 text-center">
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              关于韩漫大全
            </h1>
            <p className="text-white/80 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              致力于为中文读者提供最优质的韩漫和日漫在线阅读体验
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-8">
              品牌故事
            </h2>
            <div className="space-y-5 text-sm text-foreground-700 leading-relaxed">
              <p>
                韩漫大全创立于对漫画的热爱。我们深知每一位漫画读者在寻找高质量中文漫画资源时的困扰——资源分散、更新缓慢、翻译质量参差不齐。于是，一个汇聚韩漫和日漫的综合性阅读平台应运而生。
              </p>
              <p>
                从最初的几十部漫画，到如今涵盖<strong>韩漫大全</strong>超过<strong>10,000部</strong>漫画资源的规模，韩漫大全始终坚持<strong>免费、无删减、极速更新</strong>的核心原则。我们的团队夜以继日地工作，确保每一部<strong>好看的韩国漫画</strong>和经典日漫都能以最完整、最及时的方式呈现给读者。
              </p>
              <p>
                韩漫大全不仅是一个漫画阅读网站，更是广大漫画爱好者交流分享的社区。在这里，你可以找到热门韩漫推荐、经典日漫回顾，以及最新<strong>无删减韩漫免费在线观看</strong>的完整内容。我们相信，好的漫画值得被更多人看到，而语言不应成为阅读的障碍。
              </p>
            </div>
          </div>
        </section>

        {/* Reputation / Testimonials */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10">
              读者口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  name: '漫画迷小李',
                  text: '韩漫大全真的是我用过最好的漫画网站！更新快，画质清晰，而且完全免费。每天必刷，已经推荐给身边所有喜欢看漫画的朋友了。韩漫大全的资源太全了，好看的韩国漫画这里都有！',
                  rating: 5,
                },
                {
                  name: '日漫爱好者阿琳',
                  text: '一直想找能同时看韩漫和日漫的平台，终于找到了韩漫大全。分类清晰，搜索方便，最棒的是无删减！终于能看到完整版的漫画了，开心到飞起～',
                  rating: 5,
                },
                {
                  name: '资深读者老张',
                  text: '用韩漫大全已经超过两年了。见证了这个平台从起步到现在越来越完善。漫画资源丰富，更新速度一流，界面也很舒服。希望韩漫大全越做越好，会一直支持下去！',
                  rating: 5,
                },
              ].map((testimonial, idx) => (
                <div key={idx} className="p-6 rounded-lg bg-background-50">
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: testimonial.rating }, (_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{testimonial.text}&rdquo;
                  </p>
                  <p className="text-xs font-semibold text-foreground-900">{testimonial.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">
              有任何问题或建议，欢迎随时与我们联系
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {/* Contact Info */}
              <div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-6">联系方式</h3>
                <ul className="space-y-5">
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-500">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                      <p className="text-sm text-foreground-600">contact@hanmandq.com</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100/80 text-accent-500">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">微信公众号</p>
                      <p className="text-sm text-foreground-600">韩漫大全</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                      <i className="ri-weibo-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">新浪微博</p>
                      <p className="text-sm text-foreground-600">@韩漫大全官方</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-600">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">工作时间</p>
                      <p className="text-sm text-foreground-600">周一至周日 9:00 - 21:00</p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Contact Form */}
              <div>
                <h3 className="text-lg font-semibold text-foreground-950 mb-6">发送消息</h3>
                <form onSubmit={handleContactSubmit} className="space-y-4" data-readdy-form>
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入你的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入你的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                      placeholder="请输入你的留言（最多500字）"
                    />
                    <p className="text-xs text-foreground-400 mt-1">{contactForm.message.length}/500</p>
                  </div>

                  {/* Anti-spam honeypot */}
                  <input
                    type="text"
                    name="website_alt"
                    className="field-website-alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    className="w-full px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-accent-700 text-center">消息已发送成功，我们会尽快回复你！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-primary-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
    </>
  );
}